import React, { useState, useEffect, useContext, useMemo } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { motion, AnimatePresence } from 'framer-motion';
import logoImage from '../styles/images/logo.png';
import '@fontsource/noto-sans-hebrew';
import '../styles/css/Navbar.css';
import { LanguageContext } from '../App';

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { language, setLanguage, translations } = useContext(LanguageContext);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', language === 'he' ? 'rtl' : 'ltr');
    }
  }, [language]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const navItems = useMemo(() => translations[language].nav, [translations, language]);

  useEffect(() => {
    if (!mounted || typeof window === 'undefined') return undefined;

    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    const sectionEls = navItems
      .map((item) => document.getElementById(item.id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: 0 }
    );
    sectionEls.forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [mounted, navItems]);

  const handleScrollToSection = (sectionId) => {
    if (typeof document !== 'undefined') {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
        setMobileOpen(false);
      }
    }
  };

  if (!mounted) {
    return (
      <Box className="glass-navbar-wrapper">
        <Box className="glass-navbar">
          <span className="navbar-brand hebrew-text">
            רמי פזהר - צלם
          </span>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={`glass-navbar-wrapper ${scrolled ? 'glass-navbar-wrapper--scrolled' : ''}`}
      dir={language === 'he' ? 'rtl' : 'ltr'}
    >
      <Box className={`glass-navbar ${scrolled ? 'glass-navbar--scrolled' : ''}`}>
        <a href="#home" className="navbar-logo-link" aria-label="Home">
          <img src={logoImage} alt="Rami Pazhar Logo" className="navbar-logo" />
        </a>

        <Box component="nav" className="navbar-links">
          {navItems.map((page) => (
            <button
              key={page.id}
              type="button"
              className={`navbar-link hebrew-text ${activeSection === page.id ? 'navbar-link--active' : ''}`}
              onClick={() => handleScrollToSection(page.id)}
            >
              {activeSection === page.id && (
                <motion.span
                  layoutId="nav-active-pill"
                  className="navbar-link-pill"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                />
              )}
              <span className="navbar-link-text">{page.label}</span>
            </button>
          ))}
        </Box>

        <Box className="navbar-actions">
          <button
            type="button"
            className="navbar-lang-switch"
            onClick={() => setLanguage(language === 'he' ? 'en' : 'he')}
          >
            {language === 'he' ? 'EN' : 'עב'}
          </button>
          <IconButton
            className="navbar-menu-btn"
            aria-label="menu"
            onClick={() => setMobileOpen((prev) => !prev)}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
      </Box>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="navbar-mobile-menu"
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            {navItems.map((page) => (
              <button
                key={page.id}
                type="button"
                className={`navbar-mobile-link hebrew-text ${activeSection === page.id ? 'navbar-mobile-link--active' : ''}`}
                onClick={() => handleScrollToSection(page.id)}
              >
                {page.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}

export default Navbar;
